import CheckIcon from '@mui/icons-material/Check';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';

const toBool = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === 'yes' || normalized === '1';
  }
  return value === 1;
};

const includesToken = (value, token) =>
  typeof value === 'string' && value.toUpperCase().includes(token);

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
};

function AccountStatusCard({
  customer,
  adminLocked: adminLockedProp,
  passwordLocked: passwordLockedProp,
}) {
  const accountStatus = customer?.accountStatus || {};
  const hasAccountStatus =
    Boolean(customer?.accountStatus) && Object.keys(accountStatus).length > 0;
  const { userStatus } = accountStatus;
  const adminSuspended = accountStatus.adminSuspended ?? accountStatus.adminSuspend;
  const autoSuspended = accountStatus.autoSuspended ?? accountStatus.autoSuspend;

  const otpLocked = includesToken(userStatus, 'OTP') || toBool(autoSuspended);
  const passwordLocked =
    typeof passwordLockedProp === 'boolean'
      ? passwordLockedProp
      : includesToken(userStatus, 'PASSWORD') || toBool(autoSuspended);
  const adminLocked =
    typeof adminLockedProp === 'boolean' ? adminLockedProp : toBool(adminSuspended);

  const unlockAt =
    accountStatus.unlockAt || accountStatus.unlocksAt || accountStatus.accountUnlocksAt || '';

  const otpExpiry = formatDateTime(accountStatus.otpExpiryTime);

  const statuses = [
    {
      label: 'OTP lock',
      value: otpLocked ? 'Yes' : 'No',
      timestamp: otpLocked ? otpExpiry || unlockAt : '',
      timestampLabel: 'Account Unlock at',
      type: otpLocked ? 'warning' : 'success',
    },
    {
      label: 'Password lock',
      value: passwordLocked ? 'Yes' : 'No',
      timestamp: '',
      type: passwordLocked ? 'warning' : 'success',
    },
    {
      label: 'Admin lock',
      value: adminLocked ? 'Yes' : 'No',
      timestamp: '',
      type: adminLocked ? 'warning' : 'success',
    },
  ];

  const getStyles = (type) => {
    if (type === 'warning') {
      return {
        bg: '#F8F1DC',
        border: '#E7C55A',
        valueColor: '#A76D00',
        icon: (
          <WarningAmberOutlinedIcon
            sx={{
              fontSize: 18,
              color: '#A76D00',
            }}
          />
        ),
      };
    }

    return {
      bg: '#E6F2EC',
      border: '#A8D8BE',
      valueColor: '#159454',
      icon: (
        <CheckIcon
          sx={{
            fontSize: 18,
            color: '#159454',
          }}
        />
      ),
    };
  };

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: '1px solid #E4E8ED',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderBottom: '1px solid #E4E8ED',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FlagOutlinedIcon
            sx={{
              color: '#193452',
              fontSize: 18,
            }}
          />

          <Typography
            sx={{
              fontSize: '14.5px !important',
              fontWeight: 700,
              color: '#102A43',
            }}
          >
            Account status
          </Typography>
        </Box>

        {/* Status Cards */}
        <Box sx={{ p: 2 }}>
          {!hasAccountStatus && (
            <Typography
              sx={{
                fontSize: '14px !important',
                color: '#98A2B3',
                fontStyle: 'italic',
                textAlign: 'center',
                py: 3,
              }}
            >
              No data found
            </Typography>
          )}
          {/* One tile per row on phones, three across from tablet up. */}
          <Grid container spacing={{ xs: 1.5, sm: 2, lg: 3 }}>
            {hasAccountStatus &&
              statuses.map((status) => {
                const styles = getStyles(status.type);

                return (
                  <Grid item xs={12} sm={4} key={status.label}>
                    <Box
                      sx={{
                        backgroundColor: styles.bg,
                        border: `1px solid ${styles.border}`,
                        borderRadius: '12px',
                        p: { xs: 1.5, md: 2 },
                        minHeight: { xs: 'auto', sm: 90 },
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#5B7184',
                          fontSize: { xs: '14px !important', md: '16px !important' },
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {status.label}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {styles.icon}

                        <Typography
                          sx={{
                            color: styles.valueColor,
                            fontSize: '16px !important',
                            fontWeight: 700,
                          }}
                        >
                          {status.value}
                        </Typography>
                      </Stack>
                    </Box>
                    {status.timestamp && (
                      <Typography
                        sx={{
                          mt: 1,
                          color: '#d71714',
                          fontSize: { xs: '13px !important', md: '14px !important' },
                          fontWeight: 600,
                        }}
                      >
                        {status.timestampLabel || 'Account unlocks at'}: {status.timestamp}
                      </Typography>
                    )}
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}

AccountStatusCard.propTypes = {
  customer: PropTypes.shape({
    accountStatus: PropTypes.shape({
      status: PropTypes.string,
      userStatus: PropTypes.string,
      otpExpiryTime: PropTypes.string,
      adminSuspended: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      autoSuspended: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    }),
  }),
  adminLocked: PropTypes.bool,
  passwordLocked: PropTypes.bool,
};

AccountStatusCard.defaultProps = {
  customer: null,
  adminLocked: undefined,
  passwordLocked: undefined,
};

export default AccountStatusCard;
